/**
 * Vue composable for managing cookie consent.
 */

import { ref, reactive } from 'vue';
import {
  getConsent,
  initYandexMetrika,
  flushYandexEvents,
  trackEvent as trackYandexEvent,
} from '@/utils/yandexMetrika';

export interface ConsentCategories {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

export interface ConsentData {
  consentId: string;
  timestamp: number;
  categories: ConsentCategories;
  action: 'accept_all' | 'custom' | 'reject';
}

const API_BASE = import.meta.env.VITE_API_URL || '/api';

/**
 * Vue composable for managing cookie consent.
 */
export function useCookieConsent() {
  const showBanner = ref(!getConsent()); // Show banner if no consent yet
  const showSettings = ref(false);

  const categories = reactive<ConsentCategories>({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  });

  /**
   * Get current consent from localStorage.
   */
  function getCurrentConsent(): ConsentData | null {
    return getConsent();
  }

  /**
   * Accept all cookies.
   */
  async function acceptAll() {
    await saveConsent('accept_all', {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    });
  }

  /**
   * Reject all non-necessary cookies.
   */
  async function rejectAll() {
    await saveConsent('reject', {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    });
  }

  /**
   * Save custom consent settings.
   */
  async function saveConsent(action: string, newCategories: ConsentCategories) {
    // Persist locally first so banner stays hidden even if API fails
    const consentData: ConsentData = {
      consentId: crypto.randomUUID(),
      timestamp: Date.now(),
      categories: newCategories,
      action: action as 'accept_all' | 'custom' | 'reject',
    };
    localStorage.setItem('consent_v1', JSON.stringify(consentData));

    // Update local state immediately
    Object.assign(categories, newCategories);
    showBanner.value = false;
    showSettings.value = false;

    // Initialize analytics if consented
    if (newCategories.analytics) {
      initYandexMetrika();
      flushYandexEvents();
    }

    // Sync to API in the background (best-effort)
    try {
      const response = await fetch(`${API_BASE}/v1/compliance/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categories: newCategories, action }),
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        // Update stored consentId with server-issued one
        consentData.consentId = result.consent_id;
        localStorage.setItem('consent_v1', JSON.stringify(consentData));
      }
    } catch {
      // API unavailable — local consent already saved, nothing to do
    }
  }

  /**
   * Reopen banner for consent update.
   */
  function reopenBanner() {
    showBanner.value = true;
    showSettings.value = false;
  }

  /**
   * Open settings modal.
   */
  function openSettings() {
    showSettings.value = true;
  }

  /**
   * Close settings modal.
   */
  function closeSettings() {
    showSettings.value = false;
  }

  /**
   * Track event - will buffer if analytics not initialized.
   */
  function trackEvent(goalName: string, params?: Record<string, any>) {
    trackYandexEvent(goalName, params);
  }

  /**
   * Initialize composable on mount.
   */
  function initializeOnMount() {
    const consent = getCurrentConsent();
    if (consent) {
      Object.assign(categories, consent.categories);
      // Try to initialize analytics if consent exists
      initYandexMetrika();
    }
  }

  return {
    // State
    showBanner,
    showSettings,
    categories,

    // Methods
    getCurrentConsent,
    acceptAll,
    rejectAll,
    saveConsent,
    reopenBanner,
    openSettings,
    closeSettings,
    trackEvent,
    initializeOnMount,
  };
}
